/**
 * SuppliersPage — Issue #331.
 *
 * 供应商管理：列表（敏感字段不展示）+ 新建 + 启用/停用。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  hasPermission,
  SUPPLIER_PERMISSIONS,
} from '@/lib/procurementPermissions';
import {
  useSupplierList,
  useSupplierActions,
  type CreateSupplierDto,
  type SupplierSummaryDto,
} from '@/hooks/useSupplier';

const CATEGORY_LABELS: Record<string, string> = {
  reagent: '试剂',
  consumable: '耗材',
  service: '服务',
  equipment: '设备',
  logistics: '物流',
  other: '其他',
};

const RISK_LABELS: Record<string, string> = {
  normal: '正常',
  watch: '关注',
  high: '高风险',
  blocked: '禁用',
};
const RISK_COLORS: Record<string, string> = {
  normal: 'bg-green-100 text-green-800',
  watch: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-700',
  blocked: 'bg-red-200 text-red-800',
};

function RiskBadge({ level }: { level: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        RISK_COLORS[level] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {RISK_LABELS[level] ?? level}
    </span>
  );
}

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const { create, loading, error } = useSupplierActions();
  const [form, setForm] = useState<CreateSupplierDto>({
    code: '',
    name: '',
    category: 'reagent',
    riskLevel: 'normal',
  });
  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const ok = await create(form);
    if (ok) {
      onCreated();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">新建供应商</h2>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </div>
        )}
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">编码</label>
              <input
                required
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">名称</label>
              <input
                required
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">类别</label>
              <select
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.category}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category: e.target.value as CreateSupplierDto['category'],
                  })
                }
              >
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">风险等级</label>
              <select
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.riskLevel ?? 'normal'}
                onChange={(e) =>
                  setForm({
                    ...form,
                    riskLevel: e.target.value as CreateSupplierDto['riskLevel'],
                  })
                }
              >
                {Object.entries(RISK_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">联系人（可选）</label>
              <input
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={form.contactPerson ?? ''}
                onChange={(e) =>
                  setForm({ ...form, contactPerson: e.target.value || undefined })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">联系方式（敏感）</label>
              <input
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                placeholder="电话/邮箱/微信"
                value={form.contactChannel ?? ''}
                onChange={(e) =>
                  setForm({ ...form, contactChannel: e.target.value || undefined })
                }
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">资质摘要（敏感，列表不展示）</label>
            <textarea
              rows={2}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.qualificationSummary ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  qualificationSummary: e.target.value || undefined,
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">资质有效期（可选）</label>
            <input
              type="date"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.validUntil ?? ''}
              onChange={(e) =>
                setForm({ ...form, validUntil: e.target.value || undefined })
              }
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm"
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {loading ? '提交中…' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SuppliersPage() {
  const auth = useAuth();
  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const canRead = hasPermission(permissions, SUPPLIER_PERMISSIONS.READ);
  const canCreate = hasPermission(permissions, SUPPLIER_PERMISSIONS.CREATE);
  const canUpdate = hasPermission(permissions, SUPPLIER_PERMISSIONS.UPDATE);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, loading, error, fetchList } = useSupplierList();
  const { setEnabled, loading: actionLoading } = useSupplierActions();

  const refresh = () =>
    fetchList({
      category: categoryFilter || undefined,
      riskLevel: riskFilter || undefined,
      search: search || undefined,
    });

  useEffect(() => {
    if (canRead) {
      void refresh();
    }
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">您没有查看供应商的权限。</div>
    );
  }

  async function onToggleEnable(row: SupplierSummaryDto) {
    const ok = await setEnabled(row.id, {
      expectedVersion: row.version,
      enabled: !row.enabled,
    });
    if (ok) void refresh();
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">供应商</h1>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            新建供应商
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">类别</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">风险</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(RISK_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600">搜索</label>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="编码或名称"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm"
        >
          {loading ? '加载中…' : '查询'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">编码</th>
              <th className="px-3 py-2 text-left">名称</th>
              <th className="px-3 py-2 text-left">类别</th>
              <th className="px-3 py-2 text-left">风险</th>
              <th className="px-3 py-2 text-left">资质有效期</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rows ?? []).map((row) => (
              <tr key={row.id}>
                <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                <td className="px-3 py-2">{row.name}</td>
                <td className="px-3 py-2">
                  {CATEGORY_LABELS[row.category] ?? row.category}
                </td>
                <td className="px-3 py-2">
                  <RiskBadge level={row.riskLevel} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {row.validUntil
                    ? new Date(row.validUntil).toLocaleDateString()
                    : '—'}
                </td>
                <td className="px-3 py-2 text-xs">
                  {row.enabled ? '已启用' : '未启用'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {canUpdate &&
                      row.riskLevel !== 'blocked' &&
                      !row.enabled && (
                        <button
                          disabled={actionLoading}
                          onClick={() => void onToggleEnable(row)}
                          className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                        >
                          启用
                        </button>
                      )}
                    {canUpdate && row.enabled && (
                      <button
                        disabled={actionLoading}
                        onClick={() => void onToggleEnable(row)}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
                      >
                        停用
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(data?.rows ?? []).length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  暂无供应商
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateDialog
        key={createOpen ? 'open' : 'closed'}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void refresh()}
      />
    </div>
  );
}
